<template>
  <div class="space-y-2 pt-1">
    <form-button @click="showImageTab = true">Select Image</form-button>
    <div v-if="!currentValue" class="text-xs text-amber-400/90">
      No image linked.
    </div>
  </div>

  <FloatingPopup :width="800" :height="600" id="image-selection" v-model="showImageTab" title="Image store">
    <ImageTab :clearEnabled="false" :editable="false" @select="onImageSelect" :refreshTrigger="refreshTrigger" />
  </FloatingPopup>
</template>

<script setup>
/**
 * ImageResourcePicker.vue — Phase 6 resource-property override for `image`
 * (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md), registered in
 * resourceUIRegistry.js exactly like `dataset` registers
 * ChartDatasetPicker.vue. PropertiesPanel.vue's generic resource-property
 * loop renders this in place of the bare ResourcePicker for any
 * `resourceType: 'image'` schema property — Images need a thumbnail browser
 * (ImageTab), which a plain <select> dropdown can't provide.
 *
 * The selection flow (FloatingPopup + ImageTab in "pick one" mode,
 * clearEnabled=false/editable=false, then assign + node.fire('image')) is
 * moved here unchanged from its previous home in PropertiesPanel.vue.
 *
 * `refreshTrigger` no longer comes from a prop threaded down from App.vue
 * (the old `imageStoreUpdated` counter) — since this component already
 * imports the shared imageStore directly, it derives its own trigger by
 * watching the store's `resources` array reference, which changes on every
 * store-driven reload regardless of which component (this one, the
 * standalone Image Manager, etc.) caused it. That keeps this override
 * self-contained without requiring PropertiesPanel.vue to plumb an
 * image-specific prop through the generic property system.
 */
import { computed, ref, watch } from 'vue'
import { useImageStore } from '../../stores/imageStore.js'
import FloatingPopup from '../FloatingPopup.vue'
import ImageTab from './ImageTab.vue'

const props = defineProps({
  // Top-level img.image uses the widget event path. Part-scoped image sources
  // use the ordinary resource-property v-model path from PartPropertiesPanel.
  node: { type: Object, default: null },
  modelValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const currentValue = computed(() => props.node?.image ?? props.modelValue ?? '')

const showImageTab = ref(false)

const imageStore = useImageStore()
const refreshTrigger = ref(0)
watch(() => imageStore.resources, () => { refreshTrigger.value++ })

async function onImageSelect(image) {
  // Resolve through the store rather than trusting the emitted payload
  // directly, so this stays the single API for image reads even though the
  // emitted shape already happens to be correct.
  const resolved = image?.filename
    ? await useImageStore().getImageByFilename(image.filename)
    : null
  showImageTab.value = false
  if (resolved?.uuid) {
    if (props.node) {
      // Primary path for the img widget: point it at the resource's uuid.
      // Konva property assignment isn't reactive, so fire the change
      // explicitly (same pattern as ChartDatasetPicker).
      props.node.image = resolved.uuid
      props.node.fire('image')
    } else {
      // Part-scoped image sources use the generic property setter. Keep the
      // UUID in the Designer model; the shared style exporter turns it into
      // the established L:/filename wire representation.
      emit('update:modelValue', resolved.uuid)
    }
    return
  }
  // Fallback: no uuid available (unexpected — the store backfills one on
  // load) — resolve the old way, same as pre-uuid designs.
  if (props.node) {
    props.node.src = resolved?.src ?? image.src
    props.node.filename = resolved?.filename ?? image.filename
    props.node.adjust('reload-image')
  } else {
    emit('update:modelValue', resolved?.filename ? `L:/${resolved.filename}` : image?.src ?? '')
  }
}
</script>
